/**
 * Send later on the staff reply box: SEND with a caret beside it whose menu
 * schedules the reply instead, and the pending reply shown next to it.
 *
 * One reply can wait per ticket. Scheduling again replaces it; cancelling
 * puts its text back in the box. Text only — pictures hang off a sent message.
 */

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SendIcon from "@mui/icons-material/Send";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import {
	formatOfficeDateTime,
	isSameOfficeDay,
	officeNextAt,
	officeNextMondayAt,
} from "../../utils/date-formatting";

/** The presets' hour, office clock: the start of the working day. */
const MORNING_HOUR = 8;

export interface SendLater {
	/** ISO time of the reply waiting to go out, null when none is. */
	scheduledAt: string | null;
	onSchedule: (sendAt: Date) => void;
	onCancel: () => void;
	/** Why Send later is unavailable right now, or null when it is. */
	disabledReason: string | null;
}

interface SupportSendLaterProps {
	sendLater: SendLater;
	sendLabel: string;
	sendDisabled: boolean;
	onSend: () => void;
	dataComponent: string;
}

const SupportSendLater: React.FC<SupportSendLaterProps> = ({
	sendLater,
	sendLabel,
	sendDisabled,
	onSend,
	dataComponent,
}) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const [picking, setPicking] = useState(false);
	const [picked, setPicked] = useState<Date | null>(null);
	const now = new Date();
	const morning = officeNextAt(now, MORNING_HOUR);
	const monday = officeNextMondayAt(now, MORNING_HOUR);

	const schedule = (sendAt: Date) => {
		setAnchor(null);
		sendLater.onSchedule(sendAt);
	};

	const caret = (
		<Button
			size="small"
			aria-label="Send later"
			disabled={sendDisabled || sendLater.disabledReason !== null}
			onClick={(event) => setAnchor(event.currentTarget)}
			data-component={`${dataComponent}-send-later`}
		>
			<ArrowDropDownIcon />
		</Button>
	);

	return (
		<Stack direction="row" spacing={1} alignItems="center">
			{sendLater.scheduledAt && (
				<Chip
					icon={<ScheduleIcon />}
					color="success"
					variant="outlined"
					label={`Scheduled · ${formatOfficeDateTime(sendLater.scheduledAt)}`}
					onDelete={sendLater.onCancel}
					deleteIcon={
						<Tooltip title="Cancel and put the text back in the box">
							<CloseIcon
								data-component={`${dataComponent}-scheduled-cancel`}
							/>
						</Tooltip>
					}
					data-component={`${dataComponent}-scheduled`}
				/>
			)}
			<ButtonGroup
				variant="contained"
				size="small"
				data-component={`${dataComponent}-send-group`}
			>
				<Button
					startIcon={<SendIcon />}
					disabled={sendDisabled}
					onClick={onSend}
					data-component={`${dataComponent}-send`}
				>
					{sendLabel}
				</Button>
				{sendLater.disabledReason ? (
					<Tooltip title={sendLater.disabledReason}>
						{/* A disabled button fires no events; the span carries the tooltip. */}
						<span>{caret}</span>
					</Tooltip>
				) : (
					caret
				)}
			</ButtonGroup>
			<Menu
				anchorEl={anchor}
				open={anchor !== null}
				onClose={() => setAnchor(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				data-component={`${dataComponent}-send-later-menu`}
			>
				<MenuItem
					onClick={() => schedule(morning)}
					data-component={`${dataComponent}-send-later-tomorrow`}
				>
					{isSameOfficeDay(morning, now)
						? "Send this morning"
						: "Send tomorrow"}{" "}
					· {formatOfficeDateTime(morning)}
				</MenuItem>
				<MenuItem
					onClick={() => schedule(monday)}
					data-component={`${dataComponent}-send-later-monday`}
				>
					Send Monday · {formatOfficeDateTime(monday)}
				</MenuItem>
				<Divider />
				<MenuItem
					onClick={() => {
						setAnchor(null);
						setPicked(morning);
						setPicking(true);
					}}
					data-component={`${dataComponent}-send-later-pick`}
				>
					Pick date &amp; time…
				</MenuItem>
			</Menu>
			<Dialog
				open={picking}
				onClose={() => setPicking(false)}
				data-component={`${dataComponent}-send-later-dialog`}
			>
				<DialogTitle>Send later</DialogTitle>
				<DialogContent sx={{ pt: 1 }}>
					<DateTimePicker
						label="Send at"
						ampm={false}
						disablePast
						value={picked}
						onChange={(date) => setPicked(date as Date | null)}
						slotProps={{ textField: { size: "small", sx: { mt: 1 } } }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setPicking(false)}
						data-component={`${dataComponent}-send-later-dialog-cancel`}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						disabled={!picked || picked <= new Date()}
						onClick={() => {
							setPicking(false);
							if (picked) schedule(picked);
						}}
						data-component={`${dataComponent}-send-later-dialog-schedule`}
					>
						Schedule
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};

export default SupportSendLater;
